// Pure pricing calculation for pest control quotes.
// Zero React dependencies — fully unit-testable, reusable across all calculators.

export const DEFAULT_PRICING_CONFIG = {
  // NOTE: These values are used ONLY when the backend /api/pest-pricing endpoint is unreachable.
  // In normal operation the backend response overrides this entire object at runtime.
  // Keep this in sync with the backend's PEST_PRICING dict in server.py.
  basePrices: {
    house: { "1": 180, "2": 200, "3": 220, "4": 250, "5": 280, "6": 320 },
    unit: { "1": 150, "2": 170, "3": 190, "4": 210, "5": 240, "6": 270 },
  },
  doubleStoreyLoading: 40,
  comprehensiveMultiplier: 1.35,
  sixMonthlyDiscount: 0.20,
  termiteScaling: {
    lowThreshold: 200,
    midThreshold: 280,
    highThreshold: 350,
    inspectionPrices: { low: 150, mid: 180, high: 220 },
    barrierPrices: { low: 2500, mid: 3200, high: 4000 },
  },
  termiteBarrierDiscount: 0.25,
};

const EMPTY_RESULT = Object.freeze({
  pricePerVisit: 0,
  annualTotal: 0,
  savings: 0,
  rodentPrice: 0,
  termiteInspection: 0,
  termiteBarrier: 0,
  termiteBarrierDiscounted: 0,
});

/**
 * Compute the pest-control price breakdown from form inputs.
 *
 * @param {object} inputs - form state
 * @param {"house"|"unit"} inputs.propertyType
 * @param {string|number} inputs.bedrooms - "1".."6"
 * @param {"single"|"double"} inputs.storeys
 * @param {"standard"|"comprehensive"} inputs.treatmentType
 * @param {"oneoff"|"sixmonthly"} inputs.serviceFrequency
 * @param {boolean} inputs.rodentTreatment
 * @param {boolean} inputs.termiteAddOn
 * @param {object} pricing - pricing config (uses DEFAULT_PRICING_CONFIG shape)
 * @returns {{
 *   pricePerVisit: number,
 *   annualTotal: number,
 *   savings: number,
 *   rodentPrice: number,
 *   termiteInspection: number,
 *   termiteBarrier: number,
 *   termiteBarrierDiscounted: number,
 * }}
 */
export function computePestPricing(inputs, pricing) {
  if (!pricing) return { ...EMPTY_RESULT };

  const {
    propertyType,
    bedrooms,
    storeys,
    treatmentType,
    serviceFrequency,
    rodentTreatment,
    termiteAddOn,
  } = inputs;

  const bedroomKey = String(bedrooms);
  let basePrice =
    pricing.basePrices[propertyType]?.[bedroomKey] ?? pricing.basePrices.house["3"];

  if (storeys === "double") basePrice += pricing.doubleStoreyLoading;
  if (treatmentType === "comprehensive") {
    basePrice = Math.round(basePrice * pricing.comprehensiveMultiplier);
  }

  const rodentPrice = rodentTreatment ? calcRodentPrice(bedroomKey, storeys, treatmentType) : 0;

  let pricePerVisit = basePrice + rodentPrice;
  let annualTotal = 0;
  let savings = 0;

  if (serviceFrequency === "sixmonthly") {
    const oneOffPrice = basePrice + rodentPrice;
    pricePerVisit = Math.round(basePrice * (1 - pricing.sixMonthlyDiscount)) + rodentPrice;
    annualTotal = pricePerVisit * 2;
    savings = oneOffPrice * 2 - annualTotal;
  }

  const termite = termiteAddOn
    ? calcTermitePricing(bedroomKey, storeys, pricing)
    : { termiteInspection: 0, termiteBarrier: 0, termiteBarrierDiscounted: 0 };

  return {
    pricePerVisit,
    annualTotal,
    savings,
    rodentPrice,
    ...termite,
  };
}

function calcRodentPrice(bedroomKey, storeys, treatmentType) {
  let price = 175;
  const bn = parseInt(bedroomKey, 10);
  if (bn <= 2) price -= 25;
  else if (bn >= 5) price += 25;
  if (storeys === "double") price += 20;
  if (treatmentType === "comprehensive") price += 35;
  return price;
}

function calcTermitePricing(bedroomKey, storeys, pricing) {
  const bn = parseInt(bedroomKey, 10);

  let inspection = 150;
  if (bn >= 5) inspection += 70;
  else if (bn >= 4) inspection += 40;
  else if (bn >= 3) inspection += 20;
  if (storeys === "double") inspection += 60;

  const scaling = pricing.termiteScaling;
  let barrier;
  if (bn <= 2) barrier = scaling.barrierPrices.low;
  else if (bn <= 4) barrier = scaling.barrierPrices.mid;
  else barrier = scaling.barrierPrices.high;
  if (storeys === "double") barrier += 500;

  const barrierDiscounted = Math.round(barrier * (1 - pricing.termiteBarrierDiscount));

  return {
    termiteInspection: inspection,
    termiteBarrier: barrier,
    termiteBarrierDiscounted: barrierDiscounted,
  };
}
